'use client'

/**
 * Identity scope for client-side module caches (LUL-6956 review follow-up).
 *
 * A signed-in identity can change WITHOUT the page reloading: logout and login
 * are both `router.push`, so the JS runtime — and every module-level cache in
 * it — survives from one user to the next in the same tab. A cache keyed on
 * nothing then serves user A's data to user B with no network call at all.
 *
 * So caches that hold per-company data register a reset here, and the auth
 * provider reports the identity on every transition (restore, login, logout).
 * A change of company drops every registered cache.
 *
 * It's a registry rather than direct imports so `auth-context` never has to
 * learn about individual areas — the same reason the area registry exists.
 */

type ResetFn = () => void

const caches = new Set<ResetFn>()

/** `undefined` = no identity resolved yet this runtime; `null` = signed out. */
let scope: string | null | undefined

/**
 * Register a cache to be dropped when the signed-in company changes. Call at
 * module scope, next to the cache it clears. Returns an unregister function,
 * which only tests have any reason to call.
 */
export function registerSessionScopedCache(reset: ResetFn): () => void {
  caches.add(reset)
  return () => {
    caches.delete(reset)
  }
}

/**
 * Report the signed-in company. Clears every registered cache when it differs
 * from the last reported one. The first report of a runtime adopts the scope
 * without clearing — nothing cached before it can belong to anyone else.
 */
export function setSessionScope(companyId: string | null): void {
  const next = companyId || null
  if (scope !== undefined && scope !== next) {
    for (const reset of caches) reset()
  }
  scope = next
}

/** Tests only — forget the current scope, so the next report adopts again. */
export function resetSessionScopeForTests(): void {
  scope = undefined
}
